import React, { useState, useMemo } from 'react';
import { motion } from 'framer-motion';
import ViewModeToggle from './ViewModeToggle';
import MediaGrid from './MediaGrid';
import EnhancedMediaTable from './EnhancedMediaTable';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiSearch } = FiIcons;

const MediaDisplaySection = ({ mediaData, totalOriginalCount, hasActiveFilters }) => {
  const [viewMode, setViewMode] = useState('focus'); // 'focus' or 'pro'
  const [searchTerm, setSearchTerm] = useState('');

  // Filter data based on search term (for Focus mode)
  const filteredData = useMemo(() => {
    if (!searchTerm) return mediaData;
    return mediaData.filter(media => 
      media.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      media.product.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [mediaData, searchTerm]);

  // Calculate stats for current view
  const totalMedia = filteredData.length;
  const totalRevenue = filteredData.reduce((sum, media) => {
    return sum + Object.values(media.channels).reduce((channelSum, channel) => 
      channelSum + channel.revenue, 0
    );
  }, 0);
  const totalSpent = filteredData.reduce((sum, media) => {
    return sum + Object.values(media.channels).reduce((channelSum, channel) => 
      channelSum + channel.spent, 0
    );
  }, 0);

  return (
    <div className="space-y-6">
      {/* Header with View Mode Toggle */}
      <motion.div 
        className="bg-white rounded-xl shadow-sm border border-gray-200 p-6"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center space-y-4 lg:space-y-0">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">สื่อโฆษณาทั้งหมด</h2>
            <p className="text-sm text-gray-500 mt-1">
              แสดง {totalMedia} จาก {totalOriginalCount || mediaData.length} รายการ
              {hasActiveFilters && (
                <span className="ml-2 px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded-full font-medium">
                  กรองแล้ว
                </span>
              )}
            </p>
          </div>
          
          <div className="flex flex-col sm:flex-row items-start sm:items-center space-y-3 sm:space-y-0 sm:space-x-4">
            {/* Search for Focus Mode */}
            {viewMode === 'focus' && (
              <div className="relative">
                <SafeIcon 
                  icon={FiSearch} 
                  className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" 
                />
                <input
                  type="text"
                  placeholder="ค้นหาสื่อโฆษณา..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 pr-4 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 w-64"
                />
              </div>
            )}
            
            <ViewModeToggle 
              viewMode={viewMode} 
              onViewModeChange={setViewMode} 
            />
          </div>
        </div>

        {/* Quick Stats for Focus Mode */}
        {viewMode === 'focus' && (
          <motion.div 
            className="grid grid-cols-2 lg:grid-cols-3 gap-4 mt-6 pt-6 border-t border-gray-200"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <div className="text-center">
              <div className="text-lg font-semibold text-gray-900">{totalMedia}</div>
              <div className="text-xs text-gray-500">สื่อโฆษณา</div>
            </div>
            <div className="text-center">
              <div className="text-lg font-semibold text-green-600">฿{totalRevenue.toLocaleString()}</div>
              <div className="text-xs text-gray-500">รายได้รวม</div>
            </div>
            <div className="text-center col-span-2 lg:col-span-1">
              <div className="text-lg font-semibold text-blue-600">฿{totalSpent.toLocaleString()}</div>
              <div className="text-xs text-gray-500">งบประมาณที่ใช้</div>
            </div>
          </motion.div>
        )}
      </motion.div>

      {/* Content Based on View Mode */}
      {viewMode === 'focus' ? (
        <MediaGrid mediaData={filteredData} />
      ) : (
        <EnhancedMediaTable mediaData={mediaData} />
      )}
    </div>
  );
};

export default MediaDisplaySection;